<template>
  <div class="ojct" data-testid="organization-job-categories-tab">
    <div v-if="isDetailRoute" class="ojct-detail">
      <div
        v-if="detailLoadError"
        class="ojct-error"
        role="alert"
        data-testid="job-category-detail-error"
      >
        <p class="ojct-error__title">Job category not found</p>
        <p class="ojct-error__subtitle">
          This job category may have been deleted, or you may not have access to it.
        </p>
        <button
          type="button"
          class="ojct-error__back"
          data-testid="error-back-button"
          @click="closeDetail"
        >
          Back to Job Categories
        </button>
      </div>

      <JobCategoryDetailPage
        v-else-if="selectedCategory"
        :category="selectedCategory"
        :mode="detailMode"
        :save-error="detailSaveError"
        :rename-via-dialog="renameViaDialog"
        @back="closeDetail"
        @edit="detailMode = 'edit'"
        @cancel="onDetailCancel"
        @save="onDetailSave"
        @chatter="onRowChatter"
        @more="onDetailMore"
      />

      <div v-else class="ojct-loading" role="status" aria-live="polite" data-testid="job-category-detail-loading">
        <span class="ojct-visually-hidden">Loading job category</span>
      </div>
    </div>

    <JobCategoriesList
      v-else
      :categories="categories"
      :loading="loading"
      :filters="listFilters"
      :pagination="pagination"
      :applied-filter-chips="filterChipLabels"
      :is-account-empty="isAccountEmpty"
      @filter-change="onFilterChange"
      @more-filters="showFilterModal = true"
      @remove-filter="onRemoveFilter"
      @add-category="openWizard"
      @row-click="openDetail"
      @row-chatter="onRowChatter"
      @row-action="onRowAction"
      @positions-click="onPositionsClick"
      @needs-review="openDetailForReview"
      @page-change="onPageChange"
    />

    <FilterJobCategoriesModal
      :value="showFilterModal"
      :initial-statuses="filters.statuses"
      :initial-include-archived="filters.includeArchived"
      :initial-used="filters.used"
      @input="showFilterModal = $event"
      @apply="onFiltersApplied"
      @reset="onFiltersReset"
    />

    <DeleteJobCategoryDialog
      :show="showDeleteDialog"
      :name="(deleteCategoryTarget && deleteCategoryTarget.name) || ''"
      @onClose="closeDeleteDialog"
      @onDelete="confirmDeleteCategory"
    />

    <JobCategoryConfirmDialog
      :show="Boolean(pendingAction)"
      :action="pendingAction || 'activate'"
      :name="(actionTarget && actionTarget.name) || ''"
      :errorMessage="unarchiveError"
      @onClose="closeConfirmDialog"
      @onConfirm="confirmPendingAction"
    />

    <UnarchiveNameConflictDialog
      :show="unarchiveConflictOpen"
      objectType="job_category"
      :currentName="(unarchiveTarget && unarchiveTarget.name) || ''"
      :objectId="(unarchiveTarget && unarchiveTarget.id) || ''"
      :accountId="accountId()"
      :busy="unarchiveBusy"
      :errorMessage="unarchiveError"
      :nameError="unarchiveNameError"
      @confirm="unarchiveCategory.submitRename"
      @close="closeConfirmDialog"
      @clear-error="unarchiveCategory.clearErrors"
    />

    <RenameOrgObject
      v-if="showRenameDialog"
      :openDialog="showRenameDialog"
      OrgObjectType="Job Category"
      :name="(renameTarget && renameTarget.name) || ''"
      :placeholder="(renameTarget && renameTarget.name) || ''"
      :apiError="renameError"
      :excludeId="renameTarget && renameTarget.id"
      @onAction="confirmRename"
      @onClose="closeRenameDialog"
      @clearApiError="clearRenameError"
    />

    <ChatterDrawer
      :drawer="chatterDrawerOpen"
      iconName="org_icon_jobcategory"
      type="Job Category"
      :typeValue="chatterCategoryName"
      object-type="job_category"
      :object-id="chatterCategoryId"
      :chatterList="chatterListData"
      :totalCheckpointChatter="totalChatterCount"
      :loading="chatterLoading"
      @closeDrawer="onCloseChatterDrawer"
      @requestMoreCheckpointsChatter="onRequestMoreChatter"
    />

    <div
      v-if="showWizard"
      class="ojct-overlay"
      data-testid="add-job-category-overlay"
      @click.self="closeWizard"
    >
      <AddJobCategoryWizard
        :step="wizard.step"
        :name="wizard.name"
        :scores="wizard.scores"
        :state="wizard.name.trim() ? 'Filled' : 'Empty'"
        :api-error="wizardError"
        @update:name="wizard.name = $event; wizardError = ''"
        @update:scores="wizard.scores = $event; wizardError = ''"
        @close="closeWizard"
        @back="wizard.step = prevWizardStep(wizard.step)"
        @next="wizard.step = nextWizardStep(wizard.step)"
        @submit="onWizardSubmit"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import { ORGANIZATION_JOB_CATEGORY_DETAIL_ROUTE_NAME } from "@components/webmcp/navigation/routeConstants";

import JobCategoriesList from "@components/JobCategoriesList/JobCategoriesList.vue";
import JobCategoryDetailPage from "@components/JobCategoryDetailPage/JobCategoryDetailPage.vue";
import FilterJobCategoriesModal from "@components/FilterJobCategoriesModal/FilterJobCategoriesModal.vue";
import AddJobCategoryWizard from "@components/AddJobCategoryWizard/AddJobCategoryWizard.vue";
import DeleteJobCategoryDialog from "@components/DeleteJobCategoryDialog/DeleteJobCategoryDialog.vue";
import JobCategoryConfirmDialog from "@components/JobCategoryConfirmDialog/JobCategoryConfirmDialog.vue";
import UnarchiveNameConflictDialog from "@components/UnarchiveNameConflictDialog/UnarchiveNameConflictDialog.vue";
import { useUnarchiveOrgObject } from "@/composables/useUnarchiveOrgObject";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import RenameOrgObject from "@components/RenameOrgObject/RenameOrgObject.vue";
import { STEP } from "@components/AddJobCategoryWizard/addJobCategoryWizard.js";
import { fromApiCategory } from "@components/JobCategoriesList/jobCategoriesListRow.js";
import { NAME_IN_USE, nameConflictMessage } from "@components/constants/orgObjectMessages";
import { areDefaultFiltersApplied } from "@components/constants/filterDefaultsRegistry";
import {
  activateJobCategory,
  archiveJobCategory,
  createJobCategory,
  deleteJobCategory,
  getJobCategory,
  getListOfJobCategories,
  reactivateJobCategory,
  retireJobCategory,
  updateJobCategory,
} from "@/api/jobCategoryAPI";
import { getChatterList } from "@/api/chatterAPI";
import { transformChatterData } from "@/utils/chatterUtils";
import {
  DEFAULT_INCLUDE_ARCHIVED,
  DEFAULT_STATUSES,
  DEFAULT_USED,
  PAGE_SIZE,
  buildListParams,
  chipLabels,
  mapApiToDetailCategory,
  nextWizardStep,
  pageCountFromResponse,
  prevWizardStep,
  removeChipFromFilters,
  appliedFilterValues,
  savePayloadToUpdateBody,
  isJobCategoryInUseError,
  jobCategoryScoresErrorMessage,
  wizardPayloadToCreateBody,
} from "./organizationJobCategoriesTab.js";

const emit = defineEmits(["active-count"]);

const JOB_CATEGORIES_FILTERS_KEY = "organizationJobCategories";

const app = getCurrentInstance();
const { proxy } = app || {};

const router = useRouter();
const route = useRoute();

const loading = ref(false);
const categories = ref([]);
const showFilterModal = ref(false);
const showWizard = ref(false);
const showDeleteDialog = ref(false);
const deleteCategoryTarget = ref(null);
// Rename (ER-11890) — the shared RenameOrgObject dialog, opened from the row
// menu's Rename action; renameError renders a duplicate-name 409 inline.
const showRenameDialog = ref(false);
const renameTarget = ref(null);
const renameError = ref("");
// ER-11901 — idea-MDP-608: the detail page renames through the Rename dialog.
const renameViaDialog = computed(() =>
  Boolean(proxy && proxy.$store && proxy.$store.getters["featureFlags/ideaMDP608"])
);
// Inline messages for a backend 400 on the subfactor scores (ER-12141); shown
// instead of a toast so the wizard / edit form keeps the admin's input.
const wizardError = ref("");
const detailSaveError = ref("");
const pendingAction = ref("");
const actionTarget = ref(null);
const selectedCategory = ref(null);
const detailMode = ref("view");
const detailLoading = ref(false);
const detailLoadError = ref(false);
const page = ref(1);
const pageCount = ref(1);
const totalCount = ref(0);
// True only when the latest list request succeeded, so a failed load never reads as an empty account.
const lastLoadSucceeded = ref(false);

// Job Category chatter drawer (ER-11879) — one shared ChatterDrawer serves both
// the list-row chatter icon and the detail header's Chatter affordance.
const chatterDrawerOpen = ref(false);
const chatterListData = ref([]);
const totalChatterCount = ref(0);
const chatterCategoryId = ref(null);
const chatterCategoryName = ref("");
const chatterLoadedPages = ref({});
// True while a chatter page is in flight, so the drawer shows a spinner instead
// of flashing its empty state before the data arrives (same contract as ER-11795).
const chatterLoading = ref(false);

// ER-11744 — the drill-down is now the route (/organization/job-categories/:id)
// instead of a bare local ref, so the detail survives refresh and can be linked.
const isDetailRoute = computed(() => route?.name === ORGANIZATION_JOB_CATEGORY_DETAIL_ROUTE_NAME);

const filters = reactive({
  statuses: { ...DEFAULT_STATUSES },
  includeArchived: DEFAULT_INCLUDE_ARCHIVED,
  used: DEFAULT_USED,
  search: "",
  quickStatus: "",
});

const wizard = reactive({
  step: STEP.INFO,
  name: "",
  scores: {},
});

const listFilters = computed(() => ({
  status: filters.quickStatus,
  search: filters.search,
}));

const pagination = computed(() => ({
  page: page.value,
  pageCount: pageCount.value,
}));

const filterChipLabels = computed(() => chipLabels(filters));

// ER-12357: zero categories under the untouched default filters and no search is an empty
// account (not a filtered-empty result), so the list swaps its toolbar for "Add your first".
const isAccountEmpty = computed(() => {
  if (loading.value || !lastLoadSucceeded.value || categories.value.length > 0) return false;
  if (filters.search.trim() || filters.quickStatus) return false;
  const values = appliedFilterValues(filters);
  return values.length > 0 && areDefaultFiltersApplied(JOB_CATEGORIES_FILTERS_KEY, values);
});

function accountId() {
  return localStorage.getItem("currentAccountID");
}

function reportError(error, fallbackMessage) {
  console.error(fallbackMessage, error);
  const message = isJobCategoryInUseError(error)
    ? "This category still has positions assigned and cannot be deleted."
    : fallbackMessage;
  if (proxy && proxy.$notifications && typeof proxy.$notifications.error === "function") {
    proxy.$notifications.error(message);
  }
}

const unarchiveCategory = useUnarchiveOrgObject("job_category", {
  getAccountId: accountId,
  openConfirm: () => {
    pendingAction.value = "unarchive";
  },
  closeConfirm: () => {
    pendingAction.value = "";
    actionTarget.value = null;
  },
  onUnarchived: async ({ id: categoryId, response }) => {
    if (
      response &&
      response.data &&
      selectedCategory.value &&
      selectedCategory.value.id === categoryId
    ) {
      selectedCategory.value = mapApiToDetailCategory(response.data);
    }
    await loadCategories();
  },
  setLoading: (value) => proxy?.$loader?.setAppLoading?.(value),
});
const {
  target: unarchiveTarget,
  conflictOpen: unarchiveConflictOpen,
  busy: unarchiveBusy,
  errorMessage: unarchiveError,
  nameError: unarchiveNameError,
} = unarchiveCategory;

async function loadCategories() {
  const id = accountId();
  if (!id) return;
  loading.value = true;
  try {
    const { filters: queryFilters, pagination: queryPagination } = buildListParams(filters, {
      page: page.value,
      pageSize: PAGE_SIZE,
    });
    const { data } = await getListOfJobCategories(id, queryFilters, queryPagination);
    const results = (data && data.results) || [];
    categories.value = results.map(fromApiCategory);
    totalCount.value = Number(data && data.count) || results.length;
    pageCount.value = pageCountFromResponse(data, PAGE_SIZE);
    lastLoadSucceeded.value = true;
    emit("active-count", totalCount.value);
  } catch (error) {
    lastLoadSucceeded.value = false;
    reportError(error, "Unable to load job categories");
    categories.value = [];
    totalCount.value = 0;
    pageCount.value = 1;
    emit("active-count", 0);
  } finally {
    loading.value = false;
  }
}

function onFilterChange(next) {
  const search = next && typeof next.search === "string" ? next.search : filters.search;
  const status = next && typeof next.status === "string" ? next.status : filters.quickStatus;
  filters.search = search;
  filters.quickStatus = status;
  page.value = 1;
  loadCategories();
}

function onFiltersApplied(payload) {
  filters.statuses = { ...DEFAULT_STATUSES, ...((payload && payload.statuses) || {}) };
  filters.includeArchived = Boolean(payload && payload.includeArchived);
  filters.used = (payload && payload.used) || DEFAULT_USED;
  filters.quickStatus = "";
  showFilterModal.value = false;
  page.value = 1;
  loadCategories();
}

function onFiltersReset() {
  filters.statuses = { ...DEFAULT_STATUSES };
  filters.includeArchived = DEFAULT_INCLUDE_ARCHIVED;
  filters.used = DEFAULT_USED;
}

function onRemoveFilter(chipLabel) {
  const next = removeChipFromFilters(filters, chipLabel);
  filters.statuses = next.statuses;
  filters.includeArchived = next.includeArchived;
  filters.used = next.used;
  page.value = 1;
  loadCategories();
}

function onPageChange(nextPage) {
  page.value = nextPage;
  loadCategories();
}

function openWizard() {
  wizard.step = STEP.INFO;
  wizard.name = "";
  wizard.scores = {};
  wizardError.value = "";
  showWizard.value = true;
}

function closeWizard() {
  showWizard.value = false;
}

async function onWizardSubmit(payload) {
  const id = accountId();
  if (!id) return;
  wizardError.value = "";
  try {
    await createJobCategory(id, wizardPayloadToCreateBody(payload));
    closeWizard();
    page.value = 1;
    await loadCategories();
  } catch (error) {
    const scoresError = jobCategoryScoresErrorMessage(error);
    if (scoresError) {
      console.error("Unable to create job category", error);
      wizardError.value = scoresError;
      return;
    }
    reportError(error, "Unable to create job category");
  }
}

async function loadDetailFromRoute(categoryId) {
  const id = accountId();
  if (!id || categoryId == null) {
    selectedCategory.value = null;
    detailLoadError.value = true;
    detailLoading.value = false;
    return;
  }
  detailLoading.value = true;
  detailLoadError.value = false;
  detailSaveError.value = "";
  try {
    const { data } = await getJobCategory(id, categoryId);
    selectedCategory.value = mapApiToDetailCategory(data);
    detailMode.value = route?.query?.mode === "edit" ? "edit" : "view";
  } catch (error) {
    reportError(error, "Unable to load job category");
    selectedCategory.value = null;
    detailLoadError.value = true;
  } finally {
    detailLoading.value = false;
  }
}

function openDetail(categoryId, mode = "view") {
  if (categoryId == null || !router) return;
  router
    .push({
      name: ORGANIZATION_JOB_CATEGORY_DETAIL_ROUTE_NAME,
      params: { id: String(categoryId) },
      query: mode === "edit" ? { mode: "edit" } : {},
    })
    .catch(() => {});
}

function openDetailForReview(categoryId) {
  openDetail(categoryId, "edit");
}

function closeDetail() {
  selectedCategory.value = null;
  detailSaveError.value = "";
  detailMode.value = "view";
  detailLoadError.value = false;
  detailLoading.value = false;
  if (router) {
    router
      .push({ name: "NewOrganization", params: { tab: "categories" } })
      .catch(() => {});
  }
}

async function onDetailSave(payload) {
  const id = accountId();
  if (!id || !payload || payload.id == null) return;
  detailSaveError.value = "";
  try {
    const { data } = await updateJobCategory(id, payload.id, savePayloadToUpdateBody(payload));
    selectedCategory.value = mapApiToDetailCategory(data);
    detailMode.value = "view";
    await loadCategories();
  } catch (error) {
    if (nameConflictMessage(error)) {
      detailSaveError.value = NAME_IN_USE;
      return;
    }
    const scoresError = jobCategoryScoresErrorMessage(error);
    if (scoresError) {
      console.error("Unable to save job category", error);
      detailSaveError.value = scoresError;
      return;
    }
    reportError(error, "Unable to save job category");
  }
}

function onDetailCancel() {
  detailSaveError.value = "";
  detailMode.value = "view";
}

function onDetailMore(payload) {
  const categoryId = payload && payload.id;
  const action = payload && payload.action;
  if (categoryId == null || !action) return;
  onRowAction({ id: categoryId, action });
}

/* ── Job Category chatter drawer (ER-11879) ── */

function clearChatterLoadedPages(categoryId) {
  if (categoryId != null && chatterLoadedPages.value[categoryId]) {
    chatterLoadedPages.value[categoryId].clear();
  }
}

async function getChatterData(categoryId) {
  const id = accountId();
  const targetCategoryId = categoryId ?? chatterCategoryId.value;
  if (!id || targetCategoryId == null) return;

  const currentLength = chatterListData.value.length;
  const pageNumber = Math.floor(currentLength / 10) + 1;

  if (!chatterLoadedPages.value[targetCategoryId]) {
    chatterLoadedPages.value[targetCategoryId] = new Set();
  }
  if (chatterLoadedPages.value[targetCategoryId].has(pageNumber)) return;

  chatterLoading.value = true;
  try {
    const { data } = await getChatterList(id, targetCategoryId, "job_category", pageNumber);
    chatterLoadedPages.value[targetCategoryId].add(pageNumber);

    if (data && data.count != null) {
      totalChatterCount.value = data.count;
    }

    if (data && Array.isArray(data.results)) {
      chatterListData.value.push(...transformChatterData(data.results));
    }
  } catch (error) {
    console.error("Error fetching job category chatter data:", error);
  } finally {
    chatterLoading.value = false;
  }
}

async function reloadChatterData(categoryId) {
  if (categoryId == null) return;
  chatterListData.value = [];
  totalChatterCount.value = 0;
  clearChatterLoadedPages(categoryId);
  await getChatterData(categoryId);
}

function openChatterForCategory(categoryId, categoryName) {
  if (categoryId == null) return;
  chatterCategoryId.value = categoryId;
  chatterCategoryName.value = categoryName || "";
  chatterDrawerOpen.value = true;
  reloadChatterData(categoryId);
}

function onRowChatter(categoryId) {
  if (categoryId == null) return;
  const fromList = categories.value.find((item) => item.id === categoryId);
  const fromDetail =
    selectedCategory.value && selectedCategory.value.id === categoryId
      ? selectedCategory.value
      : null;
  const target = fromList || fromDetail;
  openChatterForCategory(categoryId, target ? target.name : "");
}

function onCloseChatterDrawer() {
  chatterDrawerOpen.value = false;
}

function onRequestMoreChatter() {
  getChatterData(chatterCategoryId.value);
}

function onRowAction({ id: categoryId, action }) {
  const id = accountId();
  if (!id || categoryId == null) return;
  if (action === "edit") {
    if (isDetailRoute.value) {
      // Overflow "Edit" from the detail page itself — just flip into edit mode.
      detailMode.value = "edit";
    } else {
      openDetail(categoryId, "edit");
    }
    return;
  }
  const fromList = categories.value.find((row) => row.id === categoryId);
  const fromDetail =
    selectedCategory.value && selectedCategory.value.id === categoryId
      ? selectedCategory.value
      : null;
  const target = fromList || fromDetail || { id: categoryId, name: "" };
  if (action === "rename") {
    // The shared menu always offers Rename (ER-11890) — open the shared
    // RenameOrgObject dialog instead of letting the action no-op.
    renameTarget.value = target;
    renameError.value = "";
    showRenameDialog.value = true;
    return;
  }
  if (action === "delete") {
    deleteCategoryTarget.value = target;
    showDeleteDialog.value = true;
    return;
  }
  if (
    action === "activate" ||
    action === "retire" ||
    action === "reactivate" ||
    action === "archive" ||
    action === "unarchive"
  ) {
    actionTarget.value = target;
    if (action === "unarchive") {
      unarchiveCategory.start({ id: target.id, name: target.name || "" });
      return;
    }
    pendingAction.value = action;
    return;
  }
}

function closeConfirmDialog() {
  pendingAction.value = "";
  actionTarget.value = null;
  unarchiveCategory.close();
}

async function confirmPendingAction() {
  const id = accountId();
  const target = actionTarget.value;
  const action = pendingAction.value;
  if (!id || !target || target.id == null || !action) return;
  if (action === "unarchive") {
    await unarchiveCategory.confirm();
    return;
  }
  try {
    let updated;
    if (action === "retire") {
      updated = await retireJobCategory(id, target.id);
    } else if (action === "activate") {
      updated = await activateJobCategory(id, target.id);
    } else if (action === "reactivate") {
      updated = await reactivateJobCategory(id, target.id);
    } else if (action === "archive") {
      updated = await archiveJobCategory(id, target.id);
    }
    // Keep the open detail page in sync after a status transition so its
    // status badge and "..." menu reflect the new state (ER-11542).
    if (
      updated &&
      updated.data &&
      selectedCategory.value &&
      selectedCategory.value.id === target.id
    ) {
      selectedCategory.value = mapApiToDetailCategory(updated.data);
    }
    closeConfirmDialog();
    await loadCategories();
  } catch (error) {
    reportError(error, `Unable to ${action} job category`);
  }
}

function closeDeleteDialog() {
  showDeleteDialog.value = false;
  deleteCategoryTarget.value = null;
}

function closeRenameDialog() {
  showRenameDialog.value = false;
  renameTarget.value = null;
  renameError.value = "";
}

function clearRenameError() {
  renameError.value = "";
}

/** Human-readable text for the rename dialog's inline error (409 duplicate names). */
function renameErrorMessage(error) {
  const data = error && error.response ? error.response.data : null;
  if (typeof data === "string") return data;
  const detail =
    (data && (data.detail || data.message || data.name)) || (error && error.message) || "";
  return Array.isArray(detail) ? String(detail[0] || "") : String(detail);
}

async function confirmRename(newName) {
  const id = accountId();
  const target = renameTarget.value;
  const name = typeof newName === "string" ? newName.trim() : "";
  if (!id || !target || target.id == null || !name) return;
  try {
    renameError.value = "";
    const response = await updateJobCategory(id, target.id, { name });
    const data = response && response.data;
    if (data && selectedCategory.value && selectedCategory.value.id === target.id) {
      selectedCategory.value = mapApiToDetailCategory(data);
    }
    closeRenameDialog();
    await loadCategories();
  } catch (error) {
    console.error("Unable to rename job category", error);
    renameError.value =
      nameConflictMessage(error) ||
      renameErrorMessage(error) ||
      "Unable to rename job category";
  }
}

async function confirmDeleteCategory() {
  const id = accountId();
  const target = deleteCategoryTarget.value;
  if (!id || !target || target.id == null) return;
  try {
    await deleteJobCategory(id, target.id);
    closeDeleteDialog();
    if (selectedCategory.value && selectedCategory.value.id === target.id) {
      closeDetail();
    }
    await loadCategories();
  } catch (error) {
    reportError(error, "Unable to delete job category");
  }
}

function onPositionsClick(categoryId) {
  if (!router) return;
  router
    .push({
      name: "NewOrganization",
      params: { tab: "positions" },
      query: { jobCategoryId: String(categoryId) },
    })
    .catch(() => {});
}

// ER-11744 — drive the drill-down from the route. Fetch on mount when the URL
// carries an id (deep-link / refresh), clear back to the list when the id leaves.
watch(
  () => ({ name: route?.name, id: route?.params?.id }),
  ({ name, id }) => {
    if (name === ORGANIZATION_JOB_CATEGORY_DETAIL_ROUTE_NAME) {
      if (id != null) {
        loadDetailFromRoute(String(id));
        return;
      }
      selectedCategory.value = null;
      detailLoadError.value = true;
      detailLoading.value = false;
      return;
    }

    selectedCategory.value = null;
    detailMode.value = "view";
    detailLoadError.value = false;
    detailLoading.value = false;
  },
  { immediate: true }
);

onMounted(loadCategories);
</script>

<style scoped>
.ojct {
  width: 100%;
}

.ojct-overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 16px 24px;
  overflow-y: auto;
  background: rgba(16, 24, 40, 0.45);
}

.ojct-detail {
  width: 100%;
}

.ojct-loading {
  padding: 24px;
}

.ojct-error {
  padding: 40px 24px;
  text-align: center;
}

.ojct-error__title {
  margin: 0 0 4px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.ojct-error__subtitle {
  margin: 0 0 16px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.ojct-error__back {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #667185;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
}

.ojct-error__back:hover {
  color: #7f56d9;
}

.ojct-error__back:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.ojct-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
